import { useAppSelector } from '@app/app.hook'
import { selectStrings } from '@app/slices/slice.app'
import { GPACareplanStepIdDetails } from '@doc/type'
import {
    FloatingArrow,
    FloatingFocusManager,
    arrow,
    autoUpdate,
    flip,
    offset,
    shift,
    useClientPoint,
    useFloating,
    useHover,
    useId,
    useInteractions,
    useTransitionStyles
} from '@floating-ui/react'
import { IDS } from '@reasonWithMe/constants'
import { BodyMapActions, BodyMapState, QuestionAnswer } from '@reasonWithMe/type'
import produce from 'immer'
import _ from 'lodash'
import { ReactElement, useMemo, useReducer, useRef, useState } from 'react'

const InformationPopover = ({ text, element }:
    { text:string | undefined, element: ReactElement}) => {
    const arrowRef = useRef(null)
    const [visible, setVisible] = useState(false)

    const { refs, floatingStyles, context } = useFloating({
        open: visible,
        placement: 'top',
        onOpenChange: (isOpen, event, reason) => {
            setVisible(isOpen)
            // event && console.log(event)
            // reason && console.log(reason)
        },
        middleware: [
            offset(10),
            flip({
                fallbackAxisSideDirection: 'end'
            }),
            shift(),
            arrow({
                element: arrowRef
            })
        ],
        whileElementsMounted: autoUpdate

    })
    const { styles } = useTransitionStyles(context)

    const clientPoint = useClientPoint(context)

    const hover = useHover(context)

    const { getReferenceProps, getFloatingProps } = useInteractions([
        hover,
        clientPoint
    ])

    const headingId = useId()

    return <>
        <div ref={refs.setReference} {...getReferenceProps()}>
            {element}
        </div>
        { visible && text && <FloatingFocusManager context={context} modal={false}>
            <div
                className={'floating-ui bodymap'}
                ref={refs.setFloating}
                style={{
                    ...floatingStyles,
                    ...styles
                }}
                aria-labelledby={headingId}
                {...getFloatingProps()}
            >
                <FloatingArrow ref={arrowRef} context={context}
                />
                {

                    <div><span>{
                        text
                    }</span></div>
                }
            </div>

        </FloatingFocusManager>}

    </>
}

interface ComponentProps {
    reasoningObj: GPACareplanStepIdDetails[
        'reasoningData']['reasoningData']['rootObject']
}

const Bodymap = ({
    reasoningObj
}: ComponentProps) => {
    const strings = useAppSelector(selectStrings)

    const bodymapRef = useRef<HTMLDivElement | null>(null)

    const questionData = reasoningObj.questionData
    const answerValue = reasoningObj.data.data.answerValue

    // new: adding a toggle for filtering from physical to non body part choices.
    const [mode] = useState<'mental'|'physical'>('physical')

    const [bodymapState, bodymapDispatch] = useReducer(
        (state: BodyMapState, action: BodyMapActions) => {
            switch (action.type) {
                case 'SET_BODY_POSITION': {
                    return produce(state, draft => {
                        draft.bodyPosition = action.value
                    })
                } case 'SET_MOUSE_IN': {
                    return produce(state, draft => {
                        draft.mouseIn = action.value
                    })
                } case 'SET_COORDINATES': {
                    return produce(state, draft => {
                        draft.coordinates = action.value
                    })
                } case 'ADD_SELECTED': {
                    return produce(state, draft => {
                        const found = _.find(draft.selected, o => {
                            return (
                                o.acqValue === action.value.acqValue &&
                                o.acqName === action.value.acqName &&
                                o.acqPosition === action.value.acqPosition

                            )
                        })

                        if (!found) {
                            draft.selected.push(action.value)
                        }
                    })
                } case 'REMOVE_SELECTED': {
                    return produce(state, draft => {
                        _.remove(draft.selected, o => {
                            return (
                                o.acqValue === action.value.acqValue &&
                                o.acqName === action.value.acqName &&
                                o.acqPosition === action.value.acqPosition
                            )
                        })
                    })
                }
                // } case 'SET_SELECTED': {
                //     return produce(state, draft => {
                //         // how do you unclick at this point.
                //         if (_.isEqual(draft.selected, action.value)) {
                //             draft.selected = {}
                //         } else {
                //             draft.selected = action.value
                //         }
                //     })
                // }
            }
        }
        , {
            bodyPosition: 'front',
            mouseIn: false,
            coordinates: [0, 0],
            selected: answerValue
        }
    )

    const setSelected = (item: QuestionAnswer) => {
        const found = _.find(bodymapState.selected, o => {
            return (
                o.acqValue === item.acqValue &&
                o.acqName === item.acqName &&
                o.acqPosition === item.acqPosition

            )
        })

        const questionAnswerAllowed = questionData?.questionAnswersAllowed

        // if 0, no limits.
        if (questionAnswerAllowed !== undefined && questionAnswerAllowed > 0) {
            if (!found) {
                // console.log('pushing body part')
                if (bodymapState.selected.length < (questionAnswerAllowed)) {
                    bodymapDispatch({
                        type: 'ADD_SELECTED',
                        value: item
                    })
                }
            } else {
                // console.log('pulling body part')
                bodymapDispatch({
                    type: 'REMOVE_SELECTED',
                    value: item
                })
            }
        } else {
            if (!found) {
                // console.log('pushing body part')
                bodymapDispatch({
                    type: 'ADD_SELECTED',
                    value: item
                })
            } else {
                // console.log('pulling body part')
                bodymapDispatch({
                    type: 'REMOVE_SELECTED',
                    value: item
                })
            }
        }
    }

    // useEffect(() => {
    //     console.log(bodymapState.selected)
    // }, [bodymapState.selected])

    const input = useMemo(() => {
        // const answerValue = questionInterface.currentReasonWithMeResponse.answerValue

        const questionsAnswers = questionData?.questionAnswers

        // the goal is to change the image to a certain kind.

        // step 1: filter out by type.
        const filteredByPosition = _.filter(questionsAnswers, (obj) => {
            return obj.acqPosition === bodymapState.bodyPosition
        })

        // iterate through each item and return matching coordinates.
        // then getting the body part that has the smallest sum.
        // if there is more than one match found.
        const filteredByCoordinates = _.filter(filteredByPosition, (obj) => {
            if (obj.acqCoords?.length) {
                return _.inRange(
                    bodymapState.coordinates[0],
                    obj.acqCoords[0]?.x,
                    obj.acqCoords[1].x
                ) &&
                _.inRange(
                    bodymapState.coordinates[1],
                    obj.acqCoords[0]?.y,
                    obj.acqCoords[1].y
                )
            } else {
                return false
            }
        })

        /** now we map the values.  */

        // console.log(filteredByCoordinates)

        const findClosestCoordinates = _.minBy(
            _.cloneDeep(filteredByCoordinates), (obj) => {
                // create new property.
                let sumX = 0; let sumY = 0

                if (obj.acqCoords?.length) {
                    sumX = obj.acqCoords[0]?.x + obj.acqCoords[1].x
                    sumY = obj.acqCoords[0]?.y + obj.acqCoords[1].y
                }

                return sumX + sumY
            }
        )

        // if the results are empty, you want to highlight the skin.
        // if (!findClosestCoordinates) {
        //     findClosestCoordinates = _.find(questionsAnswers, (obj) => {
        //         return obj.acqValue === 'skin'
        //     })
        // }

        const url = [
            '/images_new/herstelplan/bodymap/',
            bodymapState.bodyPosition === 'front' ? 'front' : 'back', '/',
            findClosestCoordinates?.acqValue || 'bodymap',
            '.png'
        ].join('')

        return <InformationPopover
            text={findClosestCoordinates?.acqName}
            element={
                <img className={'image-stack-hover'} src={url} onClick={() => {
                // console.log('image clicked: ', findClosestCoordinates)
                    if (findClosestCoordinates) {
                        setSelected(findClosestCoordinates)
                    }
                }}
                alt={strings.reason_with_me?.text.body_selection_diagram || ''} role={'button'} />
            }
        />
    }, [strings, bodymapState])

    const nonBodyPartChoices = useMemo(() => {
        const questionsAnswers = questionData?.questionAnswers

        // acqCoords that don't have elements belong here.
        const arr = _.filter(questionsAnswers, (obj) => {
            return (obj.acqCoords?.length || 0) <= 0
        })

        return <div
            className={['my-5 row row-cols-1 ',
            // { !componentDispatch ? 'row-cols-md-2' : '' }
                'btn-group-checkbox-list'].join(' ')}
            role={'group'}
        >
            {
                _.map(arr, (obj, i) => {
                    const key = [
                        'question-choice', '-', i
                    ].join('')

                    const isChecked = _.find(bodymapState.selected, o => {
                        return (
                            o.acqValue === obj.acqValue &&
                            o.acqName === obj.acqName &&
                            o.acqPosition === obj.acqPosition

                        )
                    })

                    return <div className={'col'} key={key}>
                        <input
                            type={'checkbox'}
                            className={'btn-check'}
                            id={key}
                            autoComplete={'off'}
                            checked={isChecked !== undefined}
                            onChange={() => {
                                setSelected(obj)
                            }}
                        />
                        <label className={'btn btn-dummy'} htmlFor={key} >

                            <div className={'question-checkbox'}>
                                <div className={'card justify-content-center px-3 py-2'}>
                                    <div className={'d-flex flex-column'}>
                                        <div className={'d-flex align-items-center'}>
                                            <div className={'p text-start'}><span style={{
                                                verticalAlign: 'inherit'
                                            }}>
                                                <span style={{ verticalAlign: 'inherit' }}>
                                                    {obj.acqName}
                                                </span>
                                            </span></div>
                                            <div
                                                className={'question-checkmark ms-auto'}>
                                                <i className={'fa-light fa-check mt-1 mx-auto'}></i>
                                            </div>
                                            <div
                                                className={'question-plussign ms-auto'}>
                                                <i className={'fa-light fa-plus mx-auto'}></i>
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>

                        </label>
                    </div>
                })
            }

        </div>
    }, [bodymapState])

    const showActiveBodyMap = useMemo(() => {
        const questionsAnswers = questionData?.questionAnswers

        // show questionAnswers that are visible in the bodymap.
        const arr = _.filter(questionsAnswers, (obj) => {
            return (obj.acqCoords?.length || 0) > 0
        })

        // first filter answers of current bodyposition.
        const currentBodyPosition = _.filter(bodymapState.selected, (o) => {
            return o.acqPosition === bodymapState.bodyPosition
        })

        // it will now be a list of filtered resutls
        const founds = _.filter(arr, (outer) => {
            return _.find(currentBodyPosition, (inner) => {
                return outer.acqValue === inner.acqValue &&
                outer.acqName === inner.acqName &&
                outer.acqPosition === inner.acqPosition
            }) !== undefined
        })

        // list of urls to render on top of each other
        const urls = _.map(founds, o => {
            return [
                '/images_new/herstelplan/bodymap/',
                bodymapState.bodyPosition, '/',
                o?.acqValue,
                '.png'
            ].join('')
        })

        // then push the bodymap last.
        urls.push([
            '/images_new/herstelplan/bodymap/',
            bodymapState.bodyPosition, '/',
            'bodymap',
            '.png'
        ].join(''))

        // console.log('urls: ', urls)

        return <div className={'image-stack'}>
            {bodymapState.mouseIn
                ? input
                : ''
            }

            {/* so that we don't have to keep rendering this all over again. */}

            {
                _.map(urls, (o, index) => {
                    const key = `bodymap-key-${ index }`
                    return <img key={key} className={'image'} src={o}
                        alt={strings.reason_with_me?.text.body_selection_diagram || ''}
                        role={'button'}
                    />
                })
            }
        </div>
    }, [strings, bodymapState, input])

    return <div>

        {
            mode === 'physical'
                ? <div className={'text-center pb-5'}>
                    <div className={'align-items-center justify-content-center row'}>
                        <div className={'col-auto'}>
                            <h6 className={'mb-0'}>
                                {bodymapState.bodyPosition === 'front'
                                    ? strings.app?.text.right
                                    : strings.app?.text.left}
                            </h6>
                        </div>
                        <div className={'col-auto px-0'}>
                            <div
                                className={'d-inline-block my-4'}
                                ref={(e) => {
                                    bodymapRef.current = e
                                }}
                                onMouseLeave={() => {
                                    bodymapDispatch({
                                        type: 'SET_MOUSE_IN',
                                        value: false
                                    })
                                }} onMouseMove={(e) => {
                                    if (bodymapRef.current) {
                                        const rect = bodymapRef.current.getBoundingClientRect()

                                        const x = (e.clientX - rect.left) / rect.width * 100
                                        const y = (e.clientY - rect.top) / rect.height * 100

                                        // logging purposes.

                                        // console.clear()
                                        // console.log(`Coordinates RELATIVE to element:
                                        // ${ x } and ${ y }`)

                                        bodymapDispatch({
                                            type: 'SET_COORDINATES',
                                            value: [x, y]
                                        })

                                        bodymapDispatch({
                                            type: 'SET_MOUSE_IN',
                                            value: true
                                        })
                                    }
                                } }
                            >
                                {showActiveBodyMap}
                            </div>
                        </div>
                        <div className={'col-auto'}>
                            <h6 className={'mb-0'}>
                                {bodymapState.bodyPosition === 'front'
                                    ? strings.app?.text.left
                                    : strings.app?.text.right}
                            </h6>
                        </div>
                    </div>
                    <div className={'d-flex align-items-center justify-content-center mb-4'}>
                        <label className={'form-label my-0 me-2'} htmlFor={IDS.BODYMAP.SWITCH}>
                            {
                                strings.reason_with_me?.text.bodymap.front
                            }
                        </label>
                        <div className={'form-check form-switch'}>
                            <input className={'form-check-input'}
                                type={'checkbox'} id={IDS.BODYMAP.SWITCH} checked={
                                    bodymapState.bodyPosition === 'back'
                                }
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        bodymapDispatch({
                                            type: 'SET_BODY_POSITION',
                                            value: 'back'
                                        })
                                    } else {
                                        bodymapDispatch({
                                            type: 'SET_BODY_POSITION',
                                            value: 'front'
                                        })
                                    }
                                }}
                            />
                            <label className={'form-check-label'} htmlFor={IDS.BODYMAP.SWITCH}>
                                <span style={{ verticalAlign: 'inherit' }}>
                                    <span style={{ verticalAlign: 'inherit' }}>{
                                        strings.reason_with_me?.text.bodymap.back
                                    }</span>
                                </span>
                            </label>
                        </div>
                    </div>
                </div>
                : nonBodyPartChoices
        }

    </div>
}

export default Bodymap
